/**
 * Legacy VFS adapter — wraps the existing `useVfsFiles`,
 * `useVfsUpload`, `useVfsDelete`, and `vfsApi` so AuiFiles can keep
 * talking to `/api/v1/vfs/{ownerId}/...` for conversations and skills
 * with zero behaviour change.
 *
 * Event subscription is inside `useEntries` via `useVfsFiles` (which
 * already subscribes to `vfs.file.*` for the owner).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePlatformEvents } from '@/contexts/PlatformEventContext';
import { useVfsFiles } from '@/hooks/useVfsFiles';
import { useVfsUpload as useLegacyVfsUpload } from '@/hooks/useVfsUpload';
import { useVfsDelete } from '@/hooks/useVfsDelete';
import { vfsApi } from '@/services/api';
import type {
  PlatformEvent,
  VfsFileEventData,
  VfsFileMovedEventData,
} from '@/types/events';
import type { VfsEntry } from '@/types/vfs';
import type {
  FilesBackend,
  FilesEntry,
  FilesEvent,
  UseDeleteResult,
  UseEntriesResult,
  UseFoldersResult,
  UseUploadResult,
} from './types';

import {
  addVirtualFolder,
  getVirtualFolders,
  pruneRealisedFolders,
  subscribeVirtualFolders,
} from './virtual-folders';

function vfsDataToEntry(d: VfsFileEventData, ownerId: string): FilesEntry {
  return {
    fileId: d.fileId,
    ownerId: d.ownerId ?? ownerId,
    path: d.path,
    name: d.name,
    size: d.size,
    contentType: d.contentType,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    createdBy: d.createdBy,
    etag: d.etag,
  };
}

function movedDataToEntry(d: VfsFileMovedEventData, ownerId: string): FilesEntry {
  return {
    fileId: d.fileId,
    ownerId: d.ownerId ?? ownerId,
    path: d.toPath,
    name: d.name,
    size: d.size,
    contentType: d.contentType,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    createdBy: d.createdBy,
    etag: d.etag,
  };
}

export function createVfsBackend(ownerId: string): FilesBackend {
  return {
    kind: 'vfs',
    ownerId,
    capabilities: {
      // `folders: true` here means "the user can create folders". The
      // VFS storage itself still has no folder model — `useFolders`
      // tracks them client-side, see notes at the top of this file.
      folders: true,
      indexStatus: false,
      search: false,
      copy: false,
      // VFS owners (conversations, skills) are small and bounded — keep the
      // single fetch-all `useEntries` + client-side folder synthesis. No
      // per-folder lazy loading, no recursive flat endpoint.
      lazyChildren: false,
      recursiveList: false,
    },

    useEntries(): UseEntriesResult {
      const { entries, isLoading, error, refetch } = useVfsFiles(ownerId);
      return {
        entries: entries as FilesEntry[],
        isLoading,
        error,
        refetch: async () => {
          await refetch();
        },
      };
    },

    useOnEvent(handler: (event: FilesEvent) => void) {
      const { subscribe } = usePlatformEvents();
      // Pin the handler in a ref so we resubscribe only when ownerId
      // changes — not every time the parent rebuilds the closure.
      const handlerRef = useRef(handler);
      handlerRef.current = handler;
      useEffect(() => {
        const unsub = subscribe(
          [
            { eventType: 'vfs.file.created', subjectId: ownerId },
            { eventType: 'vfs.file.updated', subjectId: ownerId },
            { eventType: 'vfs.file.deleted', subjectId: ownerId },
            { eventType: 'vfs.file.moved', subjectId: ownerId },
          ],
          (event: PlatformEvent) => {
            const fire = handlerRef.current;
            const t = event.eventType;
            if (t === 'vfs.file.moved') {
              const m = event.data as unknown as VfsFileMovedEventData;
              if (!m?.fileId) return;
              fire({
                type: 'file-moved',
                fileId: m.fileId,
                from: m.fromPath,
                to: m.toPath,
                entry: movedDataToEntry(m, ownerId),
              });
              return;
            }
            const d = event.data as unknown as VfsFileEventData;
            if (!d?.fileId) return;
            if (t === 'vfs.file.deleted') {
              fire({ type: 'file-deleted', fileId: d.fileId, path: d.path });
              return;
            }
            if (t === 'vfs.file.created') {
              fire({ type: 'file-created', entry: vfsDataToEntry(d, ownerId) });
              return;
            }
            if (t === 'vfs.file.updated') {
              fire({ type: 'file-updated', entry: vfsDataToEntry(d, ownerId) });
            }
          },
        );
        return unsub;
      }, [subscribe]);
    },

    useUpload(): UseUploadResult {
      const { jobs, uploadFile, cancel, clearDone } = useLegacyVfsUpload(ownerId);
      // Wrap `uploadFile` so a successful upload prunes any virtual
      // folder it landed inside — the path is now real on the server.
      const wrappedUploadFile = useCallback(
        async (file: File, opts?: { prefix?: string; name?: string }) => {
          const entry = await uploadFile(file, opts);
          if (entry) pruneRealisedFolders(ownerId, entry.path);
          return entry;
        },
        [uploadFile],
      );
      return { jobs, uploadFile: wrappedUploadFile, cancel, clearDone };
    },

    useDelete(): UseDeleteResult {
      return useVfsDelete(ownerId);
    },

    useFolders(): UseFoldersResult {
      // Subscribe to the module-scope store so external mutations
      // (sibling AuiFiles instances, future agent-driven mkdir, …)
      // re-render this consumer.
      const [folders, setFolders] = useState<string[]>(() =>
        Array.from(getVirtualFolders(ownerId)),
      );
      useEffect(() => {
        const unsub = subscribeVirtualFolders(ownerId, () => {
          setFolders(Array.from(getVirtualFolders(ownerId)));
        });
        return unsub;
      }, []);
      const [isCreating, setIsCreating] = useState(false);
      const mkdir = useCallback(async ({ path }: { path: string }) => {
        setIsCreating(true);
        try {
          addVirtualFolder(ownerId, path);
        } finally {
          setIsCreating(false);
        }
      }, []);
      return { folders, mkdir, isCreating };
    },

    async move({ from, to }): Promise<VfsEntry> {
      // The legacy backend ignores `overwrite` (no real folders to
      // collide against beyond filename conflicts, which 409 directly).
      return vfsApi.move(ownerId, { from, to });
    },

    async getDownloadUrl(path /*, derivative — VFS has no derivatives */) {
      const { url } = await vfsApi.getDownloadUrl(ownerId, path);
      return url;
    },

    async readText(path, version /*, derivative — VFS has no derivatives */) {
      const encodedPath = path.split('/').map(encodeURIComponent).join('/');
      const v = version ? `?v=${encodeURIComponent(version)}` : '';
      const url = `${import.meta.env.VITE_API_URL || ''}/api/v1/vfs/${encodeURIComponent(ownerId)}/${encodedPath}${v}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      return res.text();
    },
  };
}
